import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import AfricaNavbar from "@/components/AfricaNavbar";
import AfricaFooter from "@/components/AfricaFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ShieldCheck, FileSearch, Camera, AlertTriangle, FileText, Handshake,
  Search, ChevronRight, ArrowRight, Building2, Landmark, ShieldAlert,
  Layers, Cpu, KeyRound, Fingerprint, Database, Globe, MapPin, Car, Home,
  Briefcase, Package, Factory, Wheat, Gem, Truck, Ship, ScrollText,
} from "lucide-react";

/**
 * THOM Verify™ Africa — the continental parent website.
 *
 * Positioning: Africa's Independent Verification and Transaction Assurance Company.
 * NOT a marketplace. NOT a vehicle catalogue. NOT a Zimbabwe-only surface.
 * Content is intentionally broader than assets — covers organisations, records,
 * transactions and enterprise / government engagements across Africa.
 */
export default function AfricaLanding() {
  const [cert, setCert] = useState("");
  const navigate = useNavigate();

  const verify = () => {
    const q = cert.trim();
    if (!q) return;
    navigate(`/verify/${encodeURIComponent(q)}`);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <AfricaNavbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-[0.06]" style={{
          background: "radial-gradient(1200px 500px at 20% 0%, #B08C4A 0%, transparent 60%), radial-gradient(900px 400px at 90% 30%, #059669 0%, transparent 60%)",
        }} />
        <div className="relative max-w-[1500px] mx-auto px-6 pt-14 pb-24 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-14 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 rounded-sm mono text-[10.5px] uppercase tracking-[0.22em] text-white/70" data-testid="africa-hero-kicker">
                <span className="h-1.5 w-1.5 rounded-full bg-[#B08C4A]" />
                Independent · Verified · Continental
              </div>
              <h1 className="display text-[42px] sm:text-[56px] lg:text-[72px] leading-[0.98] mt-6 tracking-[-0.02em]" data-testid="africa-hero-title">
                Trust every<br />
                <span className="text-[#B08C4A]">high-value transaction</span>
                <br />across Africa.
              </h1>
              <p className="mt-6 text-white/70 text-[15.5px] leading-[1.75] max-w-[640px]" data-testid="africa-hero-body">
                THOM Verify™ provides independent verification, evidence collection, due diligence,
                digital asset passports and transaction assurance to help individuals, enterprises,
                financial institutions and governments make informed decisions across Africa.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/africa/services"><Button size="lg" className="bg-[#B08C4A] hover:bg-[#8f6f34] text-black" data-testid="africa-cta-services">Explore Verification Services <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
                <Link to="/africa/countries"><Button size="lg" variant="outline" className="border-white/25 text-white hover:bg-white/10 hover:text-white" data-testid="africa-cta-country"><Globe className="mr-2 h-4 w-4" /> Select Your Country</Button></Link>
                <Link to="/africa/enterprise"><Button size="lg" variant="outline" className="border-white/25 text-white hover:bg-white/10 hover:text-white" data-testid="africa-cta-enterprise">Request Enterprise Verification</Button></Link>
              </div>

              <div className="mt-10 max-w-[560px]" data-testid="africa-cert-lookup">
                <label className="mono text-[10.5px] uppercase tracking-[0.22em] text-white/50">Verify a Certificate · Digital Passport · Reference · QR</label>
                <div className="mt-2 flex gap-2">
                  <Input
                    value={cert}
                    onChange={(e) => setCert(e.target.value)}
                    placeholder="TVC-2026-XXXX-XXXX"
                    className="bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-white/40 h-11"
                    data-testid="africa-cert-input"
                    onKeyDown={(e) => e.key === "Enter" && verify()}
                  />
                  <Button onClick={verify} className="bg-white text-black hover:bg-white/90 h-11" data-testid="africa-cert-submit"><Search className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>

            {/* Right column: pillars */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { Icon: ShieldCheck,   k: "IDENTIFY",       t: "Asset · Party · Owner" },
                { Icon: FileSearch,    k: "VERIFY",         t: "Records · Registrars · Data" },
                { Icon: Camera,        k: "INSPECT",        t: "Physical · Technical · Specialist" },
                { Icon: AlertTriangle, k: "ASSESS RISK",    t: "Fraud · Encumbrance · Compliance" },
                { Icon: FileText,      k: "DOCUMENT",       t: "Reports · Trust Score · Certificates" },
                { Icon: Handshake,     k: "ASSURE",         t: "Escrow · Passport · Lifecycle" },
              ].map(({ Icon, k, t }) => (
                <div key={k} className="border border-white/10 bg-white/[0.02] p-5 hover:bg-white/[0.04] transition-colors" data-testid={`africa-pillar-${k.toLowerCase().replace(/\s/g,'-')}`}>
                  <Icon className="h-5 w-5 text-[#B08C4A]" />
                  <div className="mono text-[10px] uppercase tracking-[0.22em] text-white/50 mt-4">{k}</div>
                  <div className="text-[13px] text-white/90 mt-1">{t}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section B — Verification services */}
      <Section
        kicker="Verification services"
        title="Independence across every high-value category."
        body="THOM Verify™ operates a shared verification and assurance engine. Individual service families launch per country as data partnerships and regulatory clearances are secured."
        testid="africa-services"
        dark
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          <ServiceGroup
            title="Assets"
            Icon={Car}
            items={[
              { name: "AutoVerify™",           avail: "Live · Zimbabwe" },
              { name: "PropertyVerify™",       avail: "Live · Zimbabwe" },
              { name: "EquipmentVerify™",      avail: "Enterprise · pilot" },
              { name: "MachineryVerify™",      avail: "Enterprise · pilot" },
              { name: "MiningAssetVerify™",    avail: "Enterprise engagement" },
              { name: "EnergyAssetVerify™",    avail: "Enterprise engagement" },
              { name: "AgricultureVerify™",    avail: "Coming soon" },
              { name: "LuxuryAssetVerify™",    avail: "Coming soon" },
            ]}
          />
          <ServiceGroup
            title="Organisations"
            Icon={Building2}
            items={[
              { name: "BusinessVerify™",       avail: "Enterprise · pilot" },
              { name: "SupplierVerify™",       avail: "Enterprise · pilot" },
              { name: "ContractorVerify™",     avail: "Enterprise engagement" },
              { name: "IdentityVerify™",       avail: "Coming soon" },
            ]}
          />
          <ServiceGroup
            title="Transactions"
            Icon={Handshake}
            items={[
              { name: "TransactionVerify™",    avail: "Enterprise · pilot" },
              { name: "CustomsVerify™",        avail: "Enterprise engagement" },
              { name: "TradeVerify™",          avail: "Enterprise engagement" },
              { name: "DocumentVerify™",       avail: "Coming soon" },
            ]}
          />
          <ServiceGroup
            title="Shared infrastructure"
            Icon={Layers}
            items={[
              { name: "THOM Digital Passport™",  avail: "Live" },
              { name: "THOM Trust Network™",     avail: "Live" },
              { name: "THOM Escrow™",            avail: "Live · Zimbabwe" },
              { name: "THOM Verified Marketplace™", avail: "Live · Zimbabwe" },
              { name: "Digital Certificates",    avail: "Live" },
              { name: "Enterprise APIs",         avail: "Enterprise engagement" },
              { name: "Executive Intelligence",  avail: "Enterprise engagement" },
              { name: "Evidence & Due-Diligence",avail: "Enterprise engagement" },
            ]}
          />
        </div>
      </Section>

      {/* Section C — Choose a country */}
      <Section
        kicker="Country deployments"
        title="One trust infrastructure. Every African market."
        body="Zimbabwe is the first country deployment. Additional markets launch as data partnerships, regulatory clearances and inspection networks are established."
        testid="africa-countries"
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          <CountryCard
            iso="ZW" name="Zimbabwe" status="live" href="/zw"
            summary="Vehicles · Property · Sourcing · Marketplace · Escrow · Digital Passports · Certificate verification"
            cta="Enter THOM Verify Zimbabwe"
            testid="africa-country-ZW"
          />
          {[
            ["ZA","South Africa"],["BW","Botswana"],["ZM","Zambia"],
            ["MZ","Mozambique"],["NA","Namibia"],["KE","Kenya"],
            ["NG","Nigeria"],["GH","Ghana"],["TZ","Tanzania"],
            ["UG","Uganda"],["RW","Rwanda"],["CD","DRC"],
          ].map(([iso, name]) => (
            <CountryCard
              key={iso} iso={iso} name={name} status="planned"
              summary="Enterprise verification requests open · Marketplace launching soon"
              cta="Request Independent Verification"
              href={`/africa/countries/${iso}/request`}
              testid={`africa-country-${iso}`}
            />
          ))}
        </div>
        <div className="mt-6"><Link to="/africa/countries" className="text-white/70 hover:text-white text-sm inline-flex items-center gap-1">See all countries <ChevronRight className="h-3.5 w-3.5" /></Link></div>
      </Section>

      {/* Section D — Enterprise */}
      <Section
        kicker="Enterprise solutions"
        title="Purpose-built pathways for institutions that need verified truth."
        testid="africa-enterprise"
        dark
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { Icon: Landmark,   t: "Governments & Regulators",  b: "Statutory verification support, evidence records, immutable audit trails, executive dashboards."},
            { Icon: Building2,  t: "Banks & Financial Institutions", b: "KYB / KYC, collateral verification, encumbrance checks, escrow settlement, dispute evidence."},
            { Icon: ShieldAlert,t: "Insurers",                   b: "Pre-underwriting inspections, claim evidence packs, fraud triangulation, salvage verification."},
            { Icon: Car,        t: "Dealerships & Marketplaces", b: "Verified listings, buyer-protection unlocks, dealer subscriptions, THOM Verified™ badges."},
            { Icon: Factory,    t: "Mining & Industrial",        b: "Equipment provenance, transfer verification, high-value asset passports."},
            { Icon: Truck,      t: "Fleet Operators",            b: "Pre-purchase inspections, ownership continuity, resale-readiness scores, portfolio dashboards."},
            { Icon: Home,       t: "Real-estate Institutions",   b: "Title verification, encumbrance & deed checks, valuation-support evidence, transaction escrow."},
            { Icon: Ship,       t: "Trade & Logistics",          b: "CustomsVerify™ workflows, cross-border document verification, bill-of-entry reconciliation."},
            { Icon: Briefcase,  t: "Corporate Procurement",      b: "SupplierVerify™, ContractorVerify™, transaction-level assurance and evidence vault."},
          ].map((c, i) => (
            <div key={i} className="border border-white/10 bg-white/[0.02] p-5 hover:bg-white/[0.04] transition-colors" data-testid={`africa-enterprise-card-${i}`}>
              <c.Icon className="h-5 w-5 text-[#B08C4A]" />
              <h4 className="mt-4 text-[15px] font-semibold">{c.t}</h4>
              <p className="mt-2 text-[13px] text-white/70 leading-[1.6]">{c.b}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Section E — Trust infrastructure */}
      <Section
        kicker="Shared trust infrastructure"
        title="The technology stack behind verified truth."
        testid="africa-trust-infra"
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { Icon: ScrollText,  t: "THOM Digital Passport™", b: "Immutable lifecycle record of an asset — from first verification to every subsequent event."},
            { Icon: Handshake,   t: "THOM Trust Network™",    b: "Tiered directory of vetted dealers, banks, insurers, workshops and government agencies."},
            { Icon: KeyRound,    t: "THOM Escrow™",           b: "Multi-workflow escrow decoupled from payment gateway; manual admin or bank-API release."},
            { Icon: Fingerprint, t: "Digital Certificates",   b: "Tamper-evident, QR-verified certificates signed by HMAC today, upgradeable to X.509."},
            { Icon: Cpu,         t: "Enterprise APIs",        b: "RESTful endpoints for regulators, banks and enterprise partners with signed webhooks."},
            { Icon: Database,    t: "Evidence Vault",         b: "Chain-of-custody storage for inspection photos, documents, reports and dispute artefacts."},
            { Icon: FileText,    t: "Audit Trails",           b: "Immutable actor · action · target · timestamp ledger backing every case and payment."},
            { Icon: Layers,      t: "Executive Intelligence", b: "Geographic maps, risk heatmaps, portfolio scoring — governance-grade dashboards."},
          ].map((c, i) => (
            <div key={i} className="border border-white/10 bg-white/[0.02] p-5" data-testid={`africa-trust-card-${i}`}>
              <c.Icon className="h-5 w-5 text-[#B08C4A]" />
              <h4 className="mt-4 text-[15px] font-semibold">{c.t}</h4>
              <p className="mt-2 text-[13px] text-white/70 leading-[1.6]">{c.b}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Section F — Certificate verification (repeat for closing) */}
      <section className="max-w-[1500px] mx-auto px-6 py-16" data-testid="africa-verify-band">
        <div className="border border-white/15 bg-white/[0.03] p-8 lg:p-12 grid lg:grid-cols-[1.2fr_1fr] gap-8 items-center">
          <div>
            <div className="kicker text-white/70">Verify a certificate</div>
            <h3 className="display text-3xl lg:text-4xl mt-2">Trust something? Confirm it in seconds.</h3>
            <p className="mt-4 text-white/70 max-w-[560px] text-[14.5px] leading-[1.7]">
              Every THOM Verify™ certificate carries a QR-verified reference. Enter the certificate
              number, digital passport ID or verification reference to authenticate.
            </p>
          </div>
          <div>
            <label className="mono text-[10.5px] uppercase tracking-[0.22em] text-white/50">Certificate reference</label>
            <div className="mt-2 flex gap-2">
              <Input
                value={cert}
                onChange={(e) => setCert(e.target.value)}
                placeholder="TVC-2026-XXXX-XXXX"
                className="bg-white/5 border-white/20 text-white placeholder:text-white/40 h-11"
                data-testid="africa-verify-band-input"
                onKeyDown={(e) => e.key === "Enter" && verify()}
              />
              <Button onClick={verify} className="bg-[#B08C4A] hover:bg-[#8f6f34] text-black h-11" data-testid="africa-verify-band-submit"><Search className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
      </section>

      <AfricaFooter />
    </div>
  );
}

/* ----- Reusable sub-components ------------------------------------------- */

const Section = ({ kicker, title, body, testid, dark, children }) => (
  <section
    data-testid={testid}
    className={`${dark ? "bg-[#050505]" : ""} border-t border-white/5`}
  >
    <div className="max-w-[1500px] mx-auto px-6 py-16 lg:py-20">
      <div className="max-w-[820px] mb-10">
        <div className="kicker text-white/60">{kicker}</div>
        <h2 className="display text-3xl lg:text-[44px] leading-[1.05] mt-3">{title}</h2>
        {body && <p className="mt-4 text-white/70 text-[14.5px] leading-[1.75] max-w-[720px]">{body}</p>}
      </div>
      {children}
    </div>
  </section>
);

const ServiceGroup = ({ title, Icon, items }) => (
  <div className="border border-white/10 bg-white/[0.02] p-5">
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-[#B08C4A]" />
      <div className="mono text-[10.5px] uppercase tracking-[0.22em] text-white/60">{title}</div>
    </div>
    <ul className="mt-4 space-y-2.5">
      {items.map((it) => (
        <li key={it.name} className="flex items-start justify-between gap-3 text-[13px]">
          <span className="text-white/90">{it.name}</span>
          <span className="mono text-[9.5px] uppercase tracking-widest text-white/45 whitespace-nowrap">{it.avail}</span>
        </li>
      ))}
    </ul>
  </div>
);

const CountryCard = ({ iso, name, status, summary, cta, href, testid }) => (
  <div className="border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors flex flex-col" data-testid={testid}>
    <div className="p-5 flex-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-[#B08C4A]" />
          <span className="mono text-[10px] uppercase tracking-[0.22em] text-white/60">{iso}</span>
        </div>
        <span className={`mono text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${status === "live" ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-white/60"}`}>
          {status === "live" ? "Live" : "Planned"}
        </span>
      </div>
      <div className="text-xl font-semibold mt-3">THOM Verify {name}</div>
      <p className="text-[12.5px] text-white/60 mt-2 leading-[1.6]">{summary}</p>
    </div>
    <div className="p-4 border-t border-white/10">
      <Link to={href} className="text-[13px] text-white hover:text-[#B08C4A] inline-flex items-center gap-1">
        {cta} <ChevronRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  </div>
);
